// ----------------------------------------------------------------------- imports externos
import { esHueco, rolDe } from "../shared/rolPersona";
import { useEffect, useMemo, useState } from "react";

// ----------------------------------------------------------------------- imports internos
import {
  cambiarPermisosLote,
  copiarPermisos,
  darDeBajaCuenta,
  permisosDeTodos,
  reactivarCuenta,
} from "../services/permisos";
import Loading from "./Loading";
import { muertasDeVarias } from "../shared/llavesMuertas";

/* Quién puede qué: gente a la izquierda, permisos a la derecha.
 *
 * Se puede trabajar sobre VARIAS personas a la vez. Es lo que uno hace de
 * verdad — "los cuatro de portería ven lo mismo" — y de a una es media hora de
 * clicks repetidos donde además es fácil saltearse a alguien.
 *
 * Todo se guarda al instante, sin botón de guardar. Con uno, la mitad de las
 * veces alguien cambia tres cosas, se va, y descubre a la semana que no se
 * aplicó nada.
 *
 * EL BUG QUE ESTO ARREGLA: los atajos hacían un bucle llamando de a un permiso,
 * y cada vuelta calculaba el estado nuevo a partir de una copia vieja de la
 * persona. Al servidor le llegaban todos, pero la pantalla mostraba sólo el
 * último — así que parecía que no funcionaban. Ahora es una sola llamada con
 * todo el lote, y del lado de la base es una transacción.
 */

/* Los atajos siguen a los universos del menú, no a una lista propia. Cuando
 * eran una lista aparte, agregar un permiso obligaba a acordarse de sumarlo
 * también acá — y olvidarse no falla: el atajo sigue andando y reparte de
 * menos, así que la persona queda a medio dar de alta sin que nadie se entere.
 * Por eso "Taller" filtra por `grupo` y no enumera claves.
 *
 * "Sólo mirar" va primero porque es el que más se usa: casi todas las altas
 * empiezan dando lectura y recién después se decide qué puede tocar. */
/* Los que un atajo NO puede repartir, por más que empiecen con `ver.`.
 *
 * EL BUG QUE ESTO ARREGLA: "Sólo mirar" daba TODOS los `ver.*`, y cuando se
 * escribió ésos eran pantallas de trabajo. Después aparecieron `ver.direccion`
 * —dos pantallas para dos personas— y `ver.pin_ypf`, que destapa una credencial
 * de pago. Un click pensado como "dale lectura" terminó dando las dos cosas más
 * restringidas del sistema, y no falla ni avisa: la persona simplemente pasa a
 * ver algo que nadie decidió darle.
 *
 * Se siguen pudiendo dar, pero UNO POR UNO y a propósito, que es lo que la
 * palabra "restringido" significa acá. */
/* `cargar.sistema` va acá por el mismo motivo que `ver.sistema`, y con una
 * vuelta más: no sólo deja mirar el área del bot, convierte a esa persona en
 * la RESPONSABLE de contestar sus pedidos — le empiezan a aparecer en la home
 * como trabajo propio. Eso se da a propósito y de a uno, nunca de rebote por
 * apretar un paquete. */
/* `ver.ypf_cuenta` (05/10), lo mismo: la planilla de la cuenta en Otamendi.
 * Igna: «que solo tengamos permiso para ver esos datos Nicolás Cantarini y
 * yo». Un «Sólo mirar» a cualquiera se la daría sin que nadie lo decida. */
/* `borrar.garantias` (06/10), lo mismo: borra de verdad cualquier garantía,
 * las del buzón incluidas. Igna: «solo Nico y yo», y que Nico se la dé a
 * alguien si hace falta — a propósito, con su tilde. Está en el grupo Taller,
 * así que sin esto el atajo «Taller» se la daría a cada alta del taller. */
/* `autorizar.nocturnos` (09/10), lo mismo: saca una noche de la cuenta de un
 * camión, y se da de a uno —no a Satelital, que controla, ni a RRHH, que
 * sanciona—. Vive en el grupo Tráfico, así que sin esto el atajo «Tráfico» se
 * la daría a cada alta de tráfico. */
/* Las tres de Proveedores (20/10), lo mismo: son de pagos y se dan de a uno
 * (la base, además, no las reparte en lote: `permisos.de_a_uno`). Sin esto,
 * «Sólo mirar» le daría `ver.proveedores` —la bandeja de pagos— a cada alta. */
const NUNCA_EN_ATAJOS = ["ver.direccion", "ver.pin_ypf", "ver.ypf_cuenta",
                         "ver.sistema", "cargar.sistema", "admin.permisos",
                         "borrar.garantias", "autorizar.nocturnos",
                         "ver.proveedores", "cargar.proveedores", "autorizar.proveedores"];

/* Las que la base marca `permisos.de_a_uno`: `copiar_permisos` las saltea y
 * `cambiar_permisos_lote` las da de a una persona y una llave. */
const DE_A_UNO = ["ver.proveedores", "cargar.proveedores", "autorizar.proveedores"];

/* El grupo «WhatsApp» (05/10) tampoco: son los comandos del bot que escriben
 * —crear cuentas, marcar vendidos, pedir ART, cambiar el satelital de un
 * camión—, y se dan de a uno. Sin esto el atajo «Satelital», que reparte todo
 * lo que termina en `.satelital`, se llevaba `whatsapp.satelital` de rebote. */
const sinRestringidos = (f) => (p) =>
  !NUNCA_EN_ATAJOS.includes(p.clave) && p.grupo !== "WhatsApp" && f(p);

const ATAJOS = [
  { nombre: "Sólo mirar", filtra: (p) => p.clave.startsWith("ver.") },
  { nombre: "Tráfico", filtra: (p) => p.grupo === "Tráfico" },
  { nombre: "Satelital", filtra: (p) => p.clave.endsWith(".satelital") || p.clave === "ver.trafico" || p.clave === "ver.flota" },
  { nombre: "Combustible", filtra: (p) => p.clave.endsWith(".combustible") || p.clave === "ver.flota" },
  { nombre: "Documentación", filtra: (p) => p.clave.endsWith(".documentos") || p.clave === "ver.flota" || p.clave === "ver.gente" },
  { nombre: "Portería", filtra: (p) => p.clave.endsWith(".porteria") || p.clave === "ver.gente" },
  { nombre: "Taller", filtra: (p) => p.grupo === "Taller" || p.clave === "ver.flota" },
  { nombre: "Pañol", filtra: (p) => p.grupo === "Pañol" },
];

const Permisos = () => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [elegidos, setElegidos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [trabajando, setTrabajando] = useState(false);
  const [copiarDe, setCopiarDe] = useState("");
  const [motivoBaja, setMotivoBaja] = useState("");

  const cargar = () =>
    permisosDeTodos()
      .then((d) => {
        setDatos(d);
        setElegidos((prev) =>
          prev.length ? prev : d?.personas?.[0]?.mail ? [d.personas[0].mail] : []
        );
      })
      .catch((e) => setError(e.message || "No se pudo cargar"));

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const personas = useMemo(() => {
    const lista = datos?.personas || [];
    const q = busqueda.trim().toLowerCase();
    if (!q) return lista;
    return lista.filter(
      (p) =>
        (p.nombre || "").toLowerCase().includes(q) ||
        p.mail.toLowerCase().includes(q)
    );
  }, [datos, busqueda]);

  const seleccionadas = (datos?.personas || []).filter((p) =>
    elegidos.includes(p.mail)
  );

  /* Los permisos dados que no abren nada porque les falta su dependencia.
   * La regla es un DATO (`permisos.depende_de`) y el cruce vive en
   * `shared/llavesMuertas.js`, aparte y probado en una tabla. */
  const huecos = useMemo(
    () => muertasDeVarias(datos?.permisos || [], seleccionadas),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datos, elegidos],
  );

  const porGrupo = useMemo(() => {
    const g = {};
    (datos?.permisos || []).forEach((p) => (g[p.grupo] = g[p.grupo] || []).push(p));
    return g;
  }, [datos]);

  /* Con varias personas elegidas, un permiso puede estar en tres situaciones.
   * "Algunas" merece su propio aspecto: pintarlo como apagado haría creer que
   * nadie lo tiene, y tocarlo se lo sacaría a quien sí. */
  const estadoDe = (clave) => {
    if (seleccionadas.length === 0) return "no";
    const con = seleccionadas.filter((p) => (p.tiene || []).includes(clave));
    if (con.length === 0) return "no";
    if (con.length === seleccionadas.length) return "todas";
    return "algunas";
  };

  const aplicar = async (permisos, dar) => {
    if (!seleccionadas.length || !permisos.length) return;
    setTrabajando(true);
    setError(null);
    try {
      const n = await cambiarPermisosLote(
        seleccionadas.map((p) => p.mail),
        permisos,
        dar
      );
      // Se relee del servidor en vez de adivinar el estado nuevo. Adivinar es
      // justamente lo que hacía que los atajos parecieran rotos.
      await cargar();
      /* CUÁNDO SE VE EL CAMBIO, que no es lo mismo que cuándo se guardó.
       *
       * El hub lo dibuja al instante —los permisos vienen en esta misma
       * respuesta— pero las pantallas de FlotaBot (ticketera, mapa, geocercas)
       * los cachean un minuto, así que ahí puede tardar. Sin decirlo, el que
       * cambia algo y va a comprobarlo ve lo de antes y concluye que no se
       * guardó: toca de nuevo, y termina buscando el problema en el guardado,
       * que es lo único que estaba bien. */
      setAviso(
        n === 0
          ? "No hubo cambios: ya estaban así."
          : `${n} ${n === 1 ? "cambio aplicado" : "cambios aplicados"}. ` +
              "El menú ya cambió; las pantallas de FlotaBot pueden tardar " +
              "hasta un minuto."
      );
    } catch (e) {
      setError(e.message || "No se pudo guardar");
    } finally {
      setTrabajando(false);
    }
  };

  const copiar = async () => {
    if (!copiarDe || !seleccionadas.length) return;
    setTrabajando(true);
    setError(null);
    try {
      const n = await copiarPermisos(
        copiarDe,
        seleccionadas.map((p) => p.mail)
      );
      await cargar();
      /* Las llaves «de a uno» (las de pagos) la base no las copia, a
       * propósito: si la persona de origen las tiene, se dice, así un «no
       * hubo cambios» no parece que copió todo. */
      const deAUno = ((datos?.personas || []).find((p) => p.mail === copiarDe)?.tiene || [])
        .filter((k) => DE_A_UNO.includes(k));
      const salteadas = deAUno.length
        ? " Las llaves de Proveedores no se copian: se dan de a una, con su tilde."
        : "";
      setAviso(
        (n === 0
          ? "No hubo cambios: ya tenían todo lo de esa persona."
          : `${n} ${n === 1 ? "permiso copiado" : "permisos copiados"}.`) + salteadas
      );
    } catch (e) {
      setError(e.message || "No se pudo copiar");
    } finally {
      setTrabajando(false);
    }
  };

  /* DAR DE BAJA LA CUENTA (05/10). Antes no había: se le sacaban los permisos
   * y seguía entrando a las pantallas que no piden ninguno. La baja la decide
   * y la guarda la base —quién, cuándo y el motivo, que es obligatorio—, le
   * corta la clave en Supabase Auth y apaga todas sus llaves; los permisos NO
   * se tocan, así que reactivarla la devuelve con lo que tenía. De a una
   * persona por vez, a propósito: es la única acción de esta pantalla que deja
   * a alguien afuera, y en lote se hace sin mirar a quién. */
  const cambiarCuenta = async (persona, darDeBaja) => {
    setTrabajando(true);
    setError(null);
    try {
      if (darDeBaja) {
        await darDeBajaCuenta(persona.mail, motivoBaja.trim());
      } else {
        await reactivarCuenta(persona.mail);
      }
      setMotivoBaja("");
      await cargar();
      setAviso(
        darDeBaja
          ? `La cuenta de ${persona.nombre || persona.mail} quedó dada de baja: ya no puede entrar al hub.`
          : `La cuenta de ${persona.nombre || persona.mail} volvió a estar activa, con los permisos que tenía.`
      );
    } catch (e) {
      setError(e.message || "No se pudo cambiar la cuenta");
    } finally {
      setTrabajando(false);
    }
  };

  const fecha = (iso) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString("es-AR", { timeZone: "America/Argentina/Buenos_Aires" })
      : "";
  };

  const alternarElegido = (mail, conShift) => {
    setElegidos((prev) =>
      conShift
        ? prev.includes(mail)
          ? prev.filter((m) => m !== mail)
          : [...prev, mail]
        : [mail]
    );
  };

  if (error && !datos) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  if (!datos) return <Loading />;

  const todos = datos.permisos || [];

  return (
    <div className="permisos">
      <aside className="permisos-gente">
        <input
          className="permisos-buscar"
          type="search"
          placeholder="Buscar persona…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          aria-label="Buscar persona"
        />
        <div className="permisos-todos">
          <button
            className="permisos-atajo"
            onClick={() => setElegidos(personas.map((p) => p.mail))}
          >
            Elegir {personas.length}
          </button>
          {elegidos.length > 1 && (
            <button className="permisos-atajo" onClick={() => setElegidos([])}>
              Ninguna
            </button>
          )}
        </div>

        {personas.map((p) => (
          <button
            key={p.mail}
            className={
              "permisos-persona" + (elegidos.includes(p.mail) ? " activa" : "")
            }
            /* Con ctrl o cmd se suma a la selección, como en cualquier lista de
               archivos. Sin eso, elegir cinco personas es imposible. */
            onClick={(e) => alternarElegido(p.mail, e.ctrlKey || e.metaKey)}
            title="Ctrl o ⌘ para elegir varias"
          >
            <span className="permisos-nombre">{p.nombre || p.mail}</span>
            <span className={"permisos-meta"
                             + (esHueco(p) ? " sin-dato" : "")}>
              {rolDe(p)} · {(p.tiene || []).length}
              {p.baja && " · cuenta de baja"}
            </span>
          </button>
        ))}
        {personas.length === 0 && (
          <p className="permisos-vacio">Nadie coincide con esa búsqueda.</p>
        )}
      </aside>

      <section className="permisos-detalle">
        <div className="permisos-cabeza">
          <h2>
            {seleccionadas.length === 1
              ? seleccionadas[0].nombre || seleccionadas[0].mail
              : `${seleccionadas.length} personas elegidas`}
          </h2>
          <p>
            {seleccionadas.length === 1
              ? seleccionadas[0].mail
              : seleccionadas.map((p) => p.nombre || p.mail).join(", ") ||
                "Elegí a alguien de la izquierda"}
          </p>
        </div>

        {/* LLAVES QUE NO ABREN NADA.
         *
         * Pasó de verdad: se dio «Ticketera · sólo furgones» y no cambió nada.
         * El permiso estaba guardado y la pantalla decía «1 cambio aplicado»,
         * que era cierto — pero esa llave ACOTA una pantalla que abre otra
         * (`ver.ticketera`), y sin la puerta no tiene sobre qué aplicarse.
         *
         * No falla en ningún lado, y ése es el problema: el síntoma («no se
         * aplica») manda a buscarlo al guardado, que es lo único que estaba
         * bien. Sin este aviso la única salida es leerse las descripciones de
         * los 24 permisos buscando cuál depende de cuál.
         *
         * SE AVISA Y NO SE ARREGLA SOLO: dar la puerta sin preguntar abre los
         * DOS talleres, que es justo lo que el que estaba acotando no quería.
         * El botón está al lado; la decisión es de la persona. */}
        {huecos.length > 0 && (
          <div className="permisos-huecos" role="alert">
            {huecos.map((h) => (
              <div key={h.falta} className="permisos-hueco">
                <span>
                  <b>{h.inutiles.join(" y ")}</b>{" "}
                  {h.inutiles.length === 1 ? "no abre" : "no abren"} nada sin{" "}
                  <b>{h.faltaEtiqueta}</b>, que es la que abre la pantalla.
                  {h.personas.length > 0 && (
                    <span className="permisos-hueco-quien">
                      {" "}
                      Le pasa a {h.personas.join(", ")}.
                    </span>
                  )}
                </span>
                <button
                  className="permisos-atajo"
                  disabled={trabajando}
                  onClick={() => aplicar([h.falta], true)}
                  title={`Agrega ${h.faltaEtiqueta} a quien esté elegido`}
                >
                  Dar {h.faltaEtiqueta}
                </button>
              </div>
            ))}
          </div>
        )}

        {seleccionadas.length === 1 && (
          <div className="permisos-atajos permisos-cuenta">
            <span className="et">Cuenta del hub:</span>
            {seleccionadas[0].baja ? (
              <>
                <span>
                  Dada de baja
                  {fecha(seleccionadas[0].baja.en) &&
                    ` el ${fecha(seleccionadas[0].baja.en)}`}
                  {seleccionadas[0].baja.por &&
                    ` por ${seleccionadas[0].baja.por}`}
                  {seleccionadas[0].baja.motivo &&
                    ` — ${seleccionadas[0].baja.motivo}`}
                </span>
                <button
                  className="permisos-atajo"
                  disabled={trabajando}
                  onClick={() => cambiarCuenta(seleccionadas[0], false)}
                  title="Vuelve a poder entrar, con los permisos que tenía"
                >
                  Reactivar la cuenta
                </button>
              </>
            ) : (
              <>
                <input
                  className="permisos-buscar"
                  type="text"
                  placeholder="Motivo de la baja"
                  value={motivoBaja}
                  onChange={(e) => setMotivoBaja(e.target.value)}
                  aria-label="Motivo de la baja"
                />
                <button
                  className="permisos-atajo permisos-atajo--quitar"
                  disabled={trabajando || !motivoBaja.trim()}
                  onClick={() => cambiarCuenta(seleccionadas[0], true)}
                  title="Deja de poder entrar al hub. Los permisos no se tocan: reactivarla los devuelve."
                >
                  Dar de baja la cuenta
                </button>
              </>
            )}
          </div>
        )}

        {seleccionadas.length > 0 && (
          <>
            <div className="permisos-atajos">
              <span className="et">Marcar de una:</span>
              {ATAJOS.map((a) => (
                <button
                  key={a.nombre}
                  className="permisos-atajo"
                  disabled={trabajando}
                  onClick={() =>
                    aplicar(todos.filter(sinRestringidos(a.filtra)).map((p) => p.clave), true)
                  }
                  title={`Agrega los permisos de ${a.nombre}. No quita ninguno.`}
                >
                  {a.nombre}
                </button>
              ))}
              <button
                className="permisos-atajo permisos-atajo--quitar"
                disabled={trabajando}
                onClick={() => aplicar(todos.map((p) => p.clave), false)}
                title="Deja sin ningún permiso a quien esté elegido"
              >
                Quitar todos
              </button>
            </div>

            <div className="permisos-atajos">
              <span className="et">Copiar permisos de:</span>
              <select
                className="permisos-select"
                value={copiarDe}
                onChange={(e) => setCopiarDe(e.target.value)}
                aria-label="Copiar permisos de"
              >
                <option value="">Elegir persona…</option>
                {(datos.personas || [])
                  .filter((p) => !elegidos.includes(p.mail))
                  .map((p) => (
                    <option key={p.mail} value={p.mail}>
                      {p.nombre || p.mail} ({(p.tiene || []).length})
                    </option>
                  ))}
              </select>
              <button
                className="permisos-atajo"
                disabled={!copiarDe || trabajando}
                onClick={copiar}
                title="Suma lo que tiene esa persona. No quita nada."
              >
                Copiar
              </button>
            </div>

            {error && (
              <div className="hub-aviso error" role="status">
                {error}
              </div>
            )}
            {aviso && !error && (
              <div className="hub-aviso ok" role="status">
                {aviso}
              </div>
            )}

            {Object.entries(porGrupo).map(([grupo, lista]) => (
              <div key={grupo} className="permisos-grupo">
                <div className="hub-grupo">{grupo}</div>
                {lista.map((p) => {
                  const estado = estadoDe(p.clave);
                  return (
                    <div key={p.clave} className="hub-fila">
                      <div>
                        {p.etiqueta}
                        {estado === "algunas" && (
                          <span className="permisos-parcial">
                            {" "}
                            · lo tienen algunas
                          </span>
                        )}
                        {p.descripcion && (
                          <div className="desc">{p.descripcion}</div>
                        )}
                      </div>
                      <button
                        className={
                          "hub-switch" +
                          (estado === "algunas" ? " parcial" : "")
                        }
                        role="switch"
                        aria-checked={estado === "todas"}
                        aria-label={p.etiqueta}
                        disabled={trabajando}
                        /* Desde "algunas" se DA a todos, no se quita: sumar es
                           reversible de un click, y quitarle a quien ya lo
                           tenía no lo es sin acordarse de quién era. */
                        onClick={() =>
                          aplicar([p.clave], estado !== "todas")
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
};

export default Permisos;
